import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Modal, Space, Row } from 'antd';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { IMappedField } from 'app/containers/Map/slice/types';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { useTaskSlice } from 'app/containers/Task/slice';
import { selectSingleFieldTaskListState } from 'app/containers/Task/slice/selectors';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldSlice } from '../../slice';
import {
  selectDeleteFieldLoading,
  selectFieldNotes,
  selectFieldProperties,
} from '../../slice/selectors';
import EditFieldDataForm from '../EditFieldDataForm/EditFieldDataForm';

interface IProps {
  mappedField: IMappedField;
}

const FieldInfo = ({ mappedField }: IProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { actions } = useFieldSlice();
  const { actions: taskActions } = useTaskSlice();

  const envId = useSelector(selectSelectedEnvId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const deleteLoading = useSelector(selectDeleteFieldLoading);
  const { allFieldTasksArr } = useSelector(selectSingleFieldTaskListState);
  const notes = useSelector(selectFieldNotes);
  const fieldProperties = useSelector(selectFieldProperties);
  const selectedCompanyId = useSelector(selectSelectedCompanyId);
  const selectedSeasonId = useSelector(selectSelectedSeasonId);

  useEffect(() => {
    if (envId) {
      dispatch(
        actions.getFieldNotes({
          envId,
          fieldId2: mappedField.fieldParameters.fieldId2,
        }),
      );
      dispatch(
        actions.getFieldProperties({
          envId,
          fieldId2: mappedField.fieldParameters.fieldId2,
        }),
      );
    }
  }, [actions, dispatch, envId, mappedField.fieldParameters.fieldId2]);

  useEffect(() => {
    if (envId) {
      dispatch(
        taskActions.getFieldAllTaskList({
          envId,
          companyId: selectedCompanyId,
          seasonId: selectedSeasonId,
          fieldId: mappedField.fieldParameters.fieldId,
          companyGroupId,
        }),
      );
    }
  }, [
    mappedField,
    dispatch,
    envId,
    selectedCompanyId,
    selectedSeasonId,
    taskActions,
    companyGroupId,
  ]);

  const onEditClick = () => {
    dispatch(actions.toggleEditFieldDataForm(true));
  };

  const onDeleteClick = useCallback(() => {
    if (envId) {
      dispatch(
        actions.deleteField({
          envId,
          fieldId: mappedField.fieldParameters.fieldId,
        }),
      );
    }
  }, [envId, mappedField, dispatch, actions]);

  const showConfirmDeleteModal = useCallback(() => {
    Modal.confirm({
      title: t(
        'Field.Info.DeleteFieldConfirm.Title',
        'Are you sure you want to delete this field?',
      ),
      icon: <ExclamationCircleOutlined />,
      content: Boolean(allFieldTasksArr.length) ? (
        <>
          {t('Field.Info.DeleteFieldConfirm.Content', {
            defaultValue:
              'This field will be removed from the {{count}} tasks:',
            count: allFieldTasksArr.length,
          })}

          {allFieldTasksArr.map(taskInList => (
            <Row key={taskInList.id}>
              <Button type="link">{taskInList.name}</Button>
            </Row>
          ))}
        </>
      ) : (
        t(
          'Field.Info.DeleteFieldConfirm.NoTasksOnField',
          'This field does not have any tasks.',
        )
      ),

      okText: t('Field.Info.DeleteFieldConfirm.Yes', 'Yes'),
      cancelText: t('Field.Info.DeleteFieldConfirm.No', 'No'),
      onOk() {
        onDeleteClick();
      },
    });
  }, [t, allFieldTasksArr, onDeleteClick]);

  return (
    <>
      <Card
        title={t('Field.Info.Information', 'Information')}
        extra={[
          <PermissionsWrapper
            key="0"
            requiredEditPermissions={[PermissionsEnum.fieldAdministration]}
          >
            <Space>
              <Button type="primary" onClick={onEditClick}>
                {t('Field.Info.EditFieldInfo', 'Edit field data')}
              </Button>

              <Button
                danger
                loading={deleteLoading}
                onClick={showConfirmDeleteModal}
              >
                {t('Field.Info.DeleteField', 'Delete field')}
              </Button>
            </Space>
          </PermissionsWrapper>,
        ]}
      >
        <Descriptions bordered column={2} labelStyle={{ minWidth: 120 }}>
          <Descriptions.Item label={t('Field.Info.FieldId', 'Field ID')}>
            {mappedField.fieldParameters.fieldId}
          </Descriptions.Item>

          <Descriptions.Item label={t('Field.Info.Area', 'Area')}>
            {mappedField.fieldParameters.area}
          </Descriptions.Item>

          <Descriptions.Item label={t('Field.Info.FieldName', 'Field name')}>
            {mappedField.fieldParameters.name}
          </Descriptions.Item>

          <Descriptions.Item label={t('Field.Info.FieldOwner', 'Field owner')}>
            {mappedField.fieldParameters.companyName}
          </Descriptions.Item>

          <Descriptions.Item
            span={2}
            label={t('Field.Info.Metadata', 'Metadata')}
          >
            {Object.entries(fieldProperties).length ? (
              <Space wrap={true} size="large" split={'|'}>
                {Object.entries(fieldProperties).map(([key, value]) => (
                  <div>
                    {key} - {value}
                  </div>
                ))}
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item
            span={2}
            label={t('Field.Info.Notes', 'Notes')}
            contentStyle={{ whiteSpace: 'pre-line' }}
          >
            {notes || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <EditFieldDataForm
        fieldName={mappedField.fieldParameters.name}
        companyId={mappedField.fieldParameters.companyId}
        fieldId2={mappedField.fieldParameters.fieldId2}
      />
    </>
  );
};

export default FieldInfo;
